import { AccessibilityCategory } from "common/types";
import { useEffect, useState } from "react";
import AccessibilitySubItem from "./AccessibilitySubItem";
import FlagVenueAccessibility from "@/features/flag-report/FlagVenueAccessibility";
import { useAuth } from "common/hooks";
import { useAtom } from "jotai";
import {
  actionToPerformAfterLoginAtom,
  actionToPerformValueAfterLoginAtom,
} from "@/store/atoms";
import AnalyticsService from "@/services/analytics";
import { bucketUrlPrefix } from "common/config";
import { useDarkMode } from "@/hooks/useDarkMode";

type AccessibilityItemProps = {
  category: AccessibilityCategory;
  fullSize?: boolean;
  venueId: string;
  venueName: string;
  businessId: string;
  onLogin: () => void;
  venueDetails: any;
  categories: AccessibilityCategory[];
  lastUpdated: string;
  cid?: string;
  tid?: string;
  onShowDetailView?: (initialSpaceId: string) => void;
};

function AccessibilityItem({
  category,
  fullSize,
  venueId,
  venueName,
  businessId,
  onLogin,
  venueDetails,
  categories,
  lastUpdated,
  cid,
  tid,
  onShowDetailView,
}: AccessibilityItemProps) {
  const [isFlagVenueOpen, setIsFlagVenueOpen] = useState(false);
  const [selectedTagForFlag, setSelectedTagForFlag] = useState<any>(null);
  const { user, isLoggedIn } = useAuth();
  const [actionToPerformAfterLogin, setActionToPerformAfterLogin] = useAtom(
    actionToPerformAfterLoginAtom,
  );
  const [actionToPerformValueAfterLogin, setActionToPerformValueAfterLogin] =
    useAtom(actionToPerformValueAfterLoginAtom);
  const isDark = useDarkMode();

  // Generate a unique ID for the heading
  const headingId = `category-heading-${category.catId}`;

  useEffect(() => {
    if (isLoggedIn && user && actionToPerformAfterLogin) {
      if (actionToPerformAfterLogin === "flagAccessibility") {
        setSelectedTagForFlag(actionToPerformValueAfterLogin);
        setIsFlagVenueOpen(true);
      }
      setActionToPerformAfterLogin("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionToPerformAfterLogin, user, isLoggedIn]);

  const handleFlagVenueAccessibilityClose = () => {
    AnalyticsService.trackGAEventHandler("flagAccessibilityReportClose");
    AnalyticsService.trackMx("flagAccessibilityReportClose", {});
    setIsFlagVenueOpen(false);
  };

  return (
    <section aria-labelledby={headingId}>
      {/* Category Heading */}
      <h2 id={headingId} className="label-l-bold text-fg-neutral-strong py-4">
        <div className="flex items-center gap-2 w-full">
          {category.catImage && (
            <img
              src={
                bucketUrlPrefix +
                (isDark && category.catImageDark
                  ? category.catImageDark
                  : category.catImage)
              }
              alt={category.catName}
              className={`w-6 h-6 flex-shrink-0 ${isDark && !category.catImageDark ? "filter brightness-0 invert" : ""}`}
            />
          )}

          <span className="title-s-semibold text-fg-neutral-strong">
            {category.catName}
          </span>

          <span className="hidden md:inline title-s-semibold text-fg-neutral-strong">({category.tags.length})</span>
        </div>
      </h2>

      {/* Space Cards */}
      <div className="flex flex-col gap-4">
        {category.tags.map((tag, index) => {
          let reviewPhotos = tag?.reviewPhotos;
          if (category.catId === 96) {
            // Indoors VSH - Find Indoors
            const indoor = categories.find((c) => c.catId === 58);
            if (indoor) {
              if (indoor.tags[index]) {
                reviewPhotos = reviewPhotos.concat(
                  indoor.tags[index]?.reviewPhotos,
                );
              }
            }
          } else if (category.catId === 216) {
            // Outdoors VSH - Find Outdoors
            const indoor = categories.find((c) => c.catId === 172);
            if (indoor) {
              if (indoor.tags[index]?.reviewPhotos) {
                reviewPhotos = reviewPhotos.concat(
                  indoor.tags[index]?.reviewPhotos,
                );
              }
            }
          }
          return (
            <AccessibilitySubItem
              key={index}
              venueId={venueId}
              latestReviewDetailUpdatedDate={tag.latestReviewDetailUpdatedDate}
              qtags={tag.qtags}
              qtagsAll={tag.qtagsAll}
              sections={category.cat_display_sections}
              reviewPhotos={reviewPhotos}
              fullSize={fullSize}
              venueDetails={venueDetails}
              categories={categories}
              onFlag={(mainTag) => {
                if (!user || !isLoggedIn) {
                  console.log("setActionToPerformAfterLogin in the Atom");
                  setActionToPerformAfterLogin("flagAccessibility");
                  setActionToPerformValueAfterLogin(mainTag);
                  onLogin();
                  return;
                }
                setSelectedTagForFlag(mainTag);
                setIsFlagVenueOpen(true);
              }}
              lastUpdated={lastUpdated}
              cid={cid}
              tid={tid}
              onShowDetailView={onShowDetailView}
            />
          );
        })}
      </div>

      <FlagVenueAccessibility
        venueId={businessId}
        venueName={venueName}
        mainTag={selectedTagForFlag}
        catName={category.catName}
        isOpen={isFlagVenueOpen}
        onClose={() => handleFlagVenueAccessibilityClose()}
        venueDetails={venueDetails}
      />
    </section>
  );
}

export default AccessibilityItem;
